/**
 * Scaled-integer arithmetic for money, prices, and quantities.
 *
 * THE RULE: there is no `number` anywhere on the ledger path.
 *
 * A float here does not fail loudly. It produces cash balances that are off by
 * fractions of a cent, which compound silently across thousands of fills and
 * surface months later as an account that cannot be reconciled against its own
 * ledger. By then the history that would explain it is gone. See
 * ADR-0004.
 *
 * Three distinct scales, matching the Postgres column types exactly so a value
 * survives a round-trip through the database without a rescale:
 *
 *   Money  4dp   numeric(20,4)   cash, market value, cost basis, P&L
 *   Price  6dp   numeric(20,6)   quotes, limit/stop prices, fill prices
 *   Qty    8dp   numeric(24,8)   share counts, including fractional
 *
 * They are branded, so passing a Price where Money is expected is a compile
 * error rather than a value silently off by a factor of 100.
 */

declare const moneyBrand: unique symbol;
declare const priceBrand: unique symbol;
declare const qtyBrand: unique symbol;

export type Money = bigint & { readonly [moneyBrand]: true };
export type Price = bigint & { readonly [priceBrand]: true };
export type Qty = bigint & { readonly [qtyBrand]: true };

export const MONEY_SCALE = 4;
export const PRICE_SCALE = 6;
export const QTY_SCALE = 8;

const MONEY_UNIT = 10n ** BigInt(MONEY_SCALE);
const PRICE_UNIT = 10n ** BigInt(PRICE_SCALE);
const QTY_UNIT = 10n ** BigInt(QTY_SCALE);

export const ZERO_MONEY = 0n as Money;
export const ZERO_QTY = 0n as Qty;

/**
 * Half-up, away from zero: 0.5 -> 1, -0.5 -> -1.
 *
 * Chosen over banker's rounding because it is what a user checking arithmetic
 * by hand expects, and because the ledger's balance invariant
 * (sum(debits) == sum(credits)) is enforced by construction rather than by the
 * rounding mode. What matters is that rounding happens exactly once per
 * transaction, at a named boundary, not that it is statistically unbiased.
 */
function divRound(numerator: bigint, denominator: bigint): bigint {
  const negative = numerator < 0n !== denominator < 0n;
  const a = numerator < 0n ? -numerator : numerator;
  const b = denominator < 0n ? -denominator : denominator;
  const q = a / b;
  const r = a % b;
  const rounded = r * 2n >= b ? q + 1n : q;
  return negative ? -rounded : rounded;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

const DECIMAL_RE = /^(-)?(\d+)(?:\.(\d+))?$/;

/**
 * Parse a decimal string at a given scale. Extra precision is rounded, not
 * truncated — truncating "0.99999" to 4dp would quietly lose value on a
 * boundary that a user can see.
 *
 * Accepts strings only. A `number` argument is rejected at the type level
 * precisely because 0.1 + 0.2 is where this class of bug starts.
 */
function parseScaled(input: string, scale: number): bigint {
  const trimmed = input.trim();
  const match = DECIMAL_RE.exec(trimmed);
  if (!match) throw new TypeError(`not a decimal string: ${JSON.stringify(input)}`);

  const [, sign, whole, fraction = ""] = match;
  const unit = 10n ** BigInt(scale);
  const wholePart = BigInt(whole as string) * unit;

  let fractionPart = 0n;
  if (fraction.length > 0) {
    // Pad or round the fraction to exactly `scale` digits.
    const padded = fraction.padEnd(scale + 1, "0");
    const kept = BigInt(padded.slice(0, scale));
    const nextDigit = Number(padded[scale]);
    fractionPart = nextDigit >= 5 ? kept + 1n : kept;
  }

  const magnitude = wholePart + fractionPart;
  return sign === "-" ? -magnitude : magnitude;
}

function formatScaled(value: bigint, scale: number): string {
  const unit = 10n ** BigInt(scale);
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / unit;
  const fraction = (magnitude % unit).toString().padStart(scale, "0");
  return `${negative ? "-" : ""}${whole}.${fraction}`;
}

export const money = (v: string): Money => parseScaled(v, MONEY_SCALE) as Money;
export const price = (v: string): Price => parseScaled(v, PRICE_SCALE) as Price;
export const qty = (v: string): Qty => parseScaled(v, QTY_SCALE) as Qty;

/** Canonical decimal string. This is the ONLY way these values reach Postgres or JSON. */
export const formatMoney = (v: Money): string => formatScaled(v, MONEY_SCALE);
export const formatPrice = (v: Price): string => formatScaled(v, PRICE_SCALE);
export const formatQty = (v: Qty): string => formatScaled(v, QTY_SCALE);

/**
 * THE SAME NUMBER, FOR A SENTENCE A PERSON READS. Never for the wire.
 *
 * `formatMoney` is the canonical form and it is *deliberately* unfriendly:
 * four fixed places, no grouping, no symbol, because that string has to
 * round-trip through `numeric(20,4)` and through `JSON.parse` unchanged. These
 * two are for the other job — a refusal a trader reads — and they must never be
 * parsed, stored, or put in a response field a client computes from.
 *
 * **The problem this fixes was visible on production**, and it is `M5/E1`: the
 * PolicyGate wrote *"this order is worth 23969.5299 and your limit is 1000.0000
 * per order"* while the terminal's own tables, three lines above, printed
 * `23,969.53`. The terminal prints server sentences verbatim on purpose —
 * reformatting prose means parsing it — so the fix belongs on the server.
 *
 * **Two places, not four, and rounding is the honest direction here.** A
 * refusal is about magnitude: nobody reading "your limit is 1,000.00 per order"
 * needs the trailing zeros to act on it, and `23,969.53` is what every other
 * surface shows for the same value. The exact figure is never lost — it is in
 * the order row and in the audit log, which is where an exact figure belongs.
 */
export function usd(v: Money): string {
  const [whole = "0", frac = ""] = formatScaled(v, MONEY_SCALE).split(".");
  const negative = whole.startsWith("-");
  const digits = negative ? whole.slice(1) : whole;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // Round rather than truncate: 0.999 shown as "0.99" is a number that reads as
  // smaller than it is, and on a limit that is the misleading direction.
  const cents = Math.round(Number(`0.${frac.padEnd(MONEY_SCALE, "0")}`) * 100);
  const carry = cents === 100;
  const shown = carry ? "00" : String(cents).padStart(2, "0");
  const wholePart = carry ? group(BigInt(digits) + 1n) : grouped;
  return `${negative ? "-" : ""}${wholePart}.${shown}`;
}

/**
 * A share count without the eight decimal places nobody asked for.
 *
 * `formatQty` gives `10.00000000`, which reads as a precision claim rather than
 * a quantity. Trailing zeros come off and a whole number loses its point
 * entirely, so ten shares is `10` and a fractional holding keeps exactly the
 * digits it has.
 */
export function shares(v: Qty): string {
  const text = formatScaled(v, QTY_SCALE);
  const trimmed = text.replace(/\.?0+$/, "");
  return trimmed === "" || trimmed === "-" ? "0" : trimmed;
}

const group = (n: bigint): string => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * What a model call cost, from an integer count of nano-dollars.
 *
 * **Why this lives here and not in a vendor package.** Two model providers
 * needed it and each had its own rate shape — one prices per token, the other
 * meters GPU work in Neurons — but "turn an exact integer into the decimal
 * string a `numeric(12,6)` column accepts" is a money question, not a vendor
 * one. It was written inside a provider first, and the second provider is what
 * made that obviously wrong.
 *
 * **Nine places in, six places out.** Six is what `agent_runs.cost_usd` stores.
 * Nine is what the arithmetic needs: a cached input token on a $5/MTok model
 * costs half a micro-dollar, and an accumulator that cannot hold a half
 * micro-dollar reports every cheap call as free — an error in the direction that
 * flatters us. So providers accumulate in nano-dollars, exactly, and round once,
 * here.
 *
 * Half-up, so a call that genuinely cost something never reports zero. Returns a
 * plain decimal string with no exponent: `Number.prototype.toString` produces
 * `1e-7` for small values, which Postgres accepts and a person auditing a spend
 * report does not.
 */
export const COST_USD_SCALE = 6;

export function formatCostUsd(nanos: bigint): string {
  const perUnit = 10n ** BigInt(9 - COST_USD_SCALE); // nano-dollars per stored unit
  const negative = nanos < 0n;
  const magnitude = negative ? -nanos : nanos;
  const rounded = (magnitude + perUnit / 2n) / perUnit; // half-up, the one boundary
  const unit = 10n ** BigInt(COST_USD_SCALE);
  const whole = rounded / unit;
  const fraction = (rounded % unit).toString().padStart(COST_USD_SCALE, "0");
  return `${negative && rounded !== 0n ? "-" : ""}${whole}.${fraction}`;
}

/**
 * A cost back into the nano-dollars the arithmetic is done in.
 *
 * The inverse of `formatCostUsd`, and here rather than at a call site for the
 * same reason it is: summing model spend through `Money` would round it at four
 * places, and one decision on a cheap model costs about four thousandths of a
 * cent — so every call in a fold would report as free. That is an error in the
 * direction that flatters us, which is the one this file exists to refuse.
 */
export function costUsdNanos(usd: string): bigint {
  return parseScaled(usd, 9);
}

/**
 * Lossy, for display and for feeding analytics that genuinely want floats.
 * Named to make its use greppable — it must never appear on the ledger path.
 */
export const toFloatUnsafe = (v: bigint, scale: number): number => Number(formatScaled(v, scale));

// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------

export const addMoney = (a: Money, b: Money): Money => (a + b) as Money;
export const subMoney = (a: Money, b: Money): Money => (a - b) as Money;
export const negMoney = (a: Money): Money => -a as Money;
export const absMoney = (a: Money): Money => (a < 0n ? -a : a) as Money;

export const addQty = (a: Qty, b: Qty): Qty => (a + b) as Qty;
export const subQty = (a: Qty, b: Qty): Qty => (a - b) as Qty;
export const absQty = (a: Qty): Qty => (a < 0n ? -a : a) as Qty;

export const minMoney = (a: Money, b: Money): Money => (a < b ? a : b);
export const maxMoney = (a: Money, b: Money): Money => (a > b ? a : b);
export const minQty = (a: Qty, b: Qty): Qty => (a < b ? a : b);
export const maxQty = (a: Qty, b: Qty): Qty => (a > b ? a : b);

export const sumMoney = (values: readonly Money[]): Money =>
  values.reduce<bigint>((acc, v) => acc + v, 0n) as Money;

export const sumQty = (values: readonly Qty[]): Qty =>
  values.reduce<bigint>((acc, v) => acc + v, 0n) as Qty;

/**
 * qty x price -> money. The single most important function in this module:
 * every fill, every position valuation, and every cash movement goes through it.
 *
 * Scales: 8dp x 6dp = 14dp of raw product, rescaled down to 4dp. The division
 * is where rounding happens, exactly once, at a named boundary.
 */
export function notional(q: Qty, p: Price): Money {
  const raw = q * p; // scale 14
  return divRound(raw * MONEY_UNIT, QTY_UNIT * PRICE_UNIT) as Money;
}

/** money / price -> qty. How many shares a given amount of cash buys. */
export function affordableQty(cash: Money, p: Price): Qty {
  if (p <= 0n) throw new RangeError("affordableQty requires a positive price");
  return divRound(cash * QTY_UNIT * PRICE_UNIT, p * MONEY_UNIT) as Qty;
}

/** Weighted-average price across fills. Used for avg entry price and avg fill price. */
export function weightedAveragePrice(
  legs: readonly { qty: Qty; price: Price }[],
): Price | undefined {
  let totalQty = 0n;
  let totalValue = 0n; // scale 14
  for (const leg of legs) {
    totalQty += leg.qty;
    totalValue += leg.qty * leg.price;
  }
  if (totalQty === 0n) return undefined;
  return divRound(totalValue, totalQty) as Price;
}

/** Apply basis points to a price. Positive bps moves up, negative moves down. */
export function applyBps(p: Price, bps: number): Price {
  if (!Number.isInteger(bps)) throw new TypeError("bps must be an integer");
  return divRound(p * BigInt(10_000 + bps), 10_000n) as Price;
}

/** Fraction of a money amount, as basis points. Used for percentage commissions. */
export function bpsOfMoney(m: Money, bps: number): Money {
  if (!Number.isInteger(bps)) throw new TypeError("bps must be an integer");
  return divRound(m * BigInt(bps), 10_000n) as Money;
}
